import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './TopBar.css';

const TopBar = ({ examTitle, sectionTitle, timeLeft, isFitMode, onToggleFit, audioUrl, isStrictAudio }) => {
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const getSectionEmoji = (title) => {
    if (!title) return '📚';
    const t = title.toUpperCase();
    if (t.includes('LISTEN')) return '🎧';
    if (t.includes('READ')) return '📖';
    if (t.includes('WRIT')) return '✏️';
    if (t.includes('SPEAK')) return '🎤';
    return '⭐';
  };

  const getAudioSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${ASSET_BASE_URL}${url}`;
  };

  const handlePlayStrict = () => {
    if (audioRef.current) {
      if (isStrictAudio) {
        if (!hasStarted) {
          audioRef.current.play();
          setIsPlaying(true);
          setHasStarted(true);
        }
      } else {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
          setHasStarted(true);
        }
      }
    }
  };

  const handleSeek = (e) => {
    if (!isStrictAudio && audioRef.current) {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.min(Math.max(clickX / width, 0), 1);
      const newTime = percentage * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setProgress(percentage * 100);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration;
      if (total > 0) {
        setProgress((current / total) * 100);
      }
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setProgress(100);
  };

  useEffect(() => {
    setHasStarted(false);
    setIsPlaying(false);
    setProgress(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [audioUrl]);

  const isLowTime = timeLeft !== null && timeLeft !== undefined && timeLeft <= 60;
  const sectionEmoji = getSectionEmoji(sectionTitle);

  return (
    <header className="bg-white dark:bg-card-dark shadow-sm z-50 sticky top-0 border-b-2 border-amber-100 dark:border-slate-700 px-4 sm:px-6 py-2 flex items-center justify-between transition-colors duration-300 min-h-[60px] gap-4">
      {/* Left: Home + Section Badge + Title */}
      <div className="flex items-center gap-3 shrink-0">
        <Link to="/" className="w-9 h-9 rounded-2xl flex items-center justify-center text-slate-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-slate-800 transition-all border-2 border-slate-100 dark:border-slate-700">
          <span className="material-symbols-outlined text-xl">home</span>
        </Link>
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-xl shadow-md shadow-amber-200 border-2 border-white">
          {sectionEmoji}
        </div>
        <div className="hidden sm:flex flex-col justify-center">
          <h1 className="text-sm md:text-base font-black text-slate-800 dark:text-blue-400 leading-tight truncate max-w-[220px]">{examTitle}</h1>
          {sectionTitle && (
            <span className="mt-0.5 text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-700 leading-none truncate max-w-[220px]">
              {sectionTitle}
            </span>
          )}
        </div>
      </div>

      {/* Center: Audio Player */}
      {audioUrl && (
        <div className="flex-1 flex justify-center min-w-0">
          <div className="flex items-center gap-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:bg-slate-800 px-4 py-2 rounded-2xl w-full max-w-md border-2 border-amber-200 dark:border-slate-600 shadow-sm">
            <audio
              ref={audioRef}
              src={getAudioSrc(audioUrl)}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleAudioEnded}
              controlsList="nodownload"
            />
            <button
              onClick={handlePlayStrict}
              disabled={isStrictAudio && hasStarted}
              className={`w-9 h-9 rounded-xl flex items-center justify-center text-white transition-all shadow-md ${isStrictAudio && hasStarted
                  ? 'bg-slate-300 cursor-not-allowed'
                  : 'bg-gradient-to-br from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 hover:scale-105 shadow-amber-200'
                }`}
            >
              <span className="material-symbols-outlined text-xl">
                {isStrictAudio ? (hasStarted ? 'volume_up' : 'play_arrow') : (isPlaying ? 'pause' : 'play_arrow')}
              </span>
            </button>
            <div
              className={`flex-1 h-3 bg-amber-100 dark:bg-slate-700 rounded-full overflow-hidden ${!isStrictAudio ? 'cursor-pointer' : ''} border border-amber-200 dark:border-slate-600`}
              onClick={handleSeek}
            >
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-300 ease-linear rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Right: Fit Mode + Timer */}
      <div className="flex items-center gap-3 shrink-0">
        {onToggleFit && (
          <button
            onClick={onToggleFit}
            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all border-2 ${isFitMode
                ? 'bg-amber-100 text-amber-600 border-amber-300 dark:bg-amber-900/30'
                : 'text-slate-500 border-slate-100 hover:bg-amber-50 hover:border-amber-200 dark:hover:bg-slate-800 dark:border-slate-700'
              }`}
            title={isFitMode ? 'Exit Fit Mode' : 'Fit to Screen'}
          >
            <span className="material-symbols-outlined text-xl">
              {isFitMode ? 'fullscreen_exit' : 'fullscreen'}
            </span>
          </button>
        )}

        {timeLeft !== undefined && timeLeft !== null && (
          <div className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-sm shadow-md border-2 transition-all ${isLowTime
              ? 'bg-red-500 text-white border-red-400 animate-pulse shadow-red-200'
              : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white border-amber-300 shadow-amber-200'
            }`}>
            <span className="material-symbols-outlined text-base">timer</span>
            <span className="font-display text-base">{formatTime(timeLeft)}</span>
          </div>
        )}
      </div>
    </header>
  );
};

export default TopBar;